import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ApiService from '../services/api';
import { Article as ArticleType } from '../types';
import ShareButtons from '../components/ShareButtons';

const Article: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return;
      
      try {
        const articleData = await ApiService.getArticleBySlug(slug);
        setArticle(articleData);
      } catch (err) {
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  const handleEngagement = async (action: 'share') => {
    if (!article) return;
    
    try {
      await ApiService.engageWithArticle(article.id, action);
      // Update local state optimistically
      setArticle(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          shares: prev.shares + 1
        };
      });
    } catch (err) {
      console.error(`Error ${action}ing article:`, err);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  if (error || !article) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
        <p className="text-lg mb-8">The article you're looking for doesn't exist.</p>
        <Link to="/" className="skeuomorphic-btn text-black py-2 px-6 rounded-full">
          Go Home
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-1 justify-center py-10 px-6 bg-stone-50 text-stone-900">
      <div className="flex w-full max-w-7xl gap-12">
        <article className="flex-1">
          <div className="mb-8">
            <div className="flex items-center gap-2 text-sm text-stone-500 mb-4">
              <Link className="hover:text-stone-800" to="/search">Articles</Link>
              <span>/</span>
              <span className="text-stone-800 font-medium">{article.title}</span>
            </div>
            <h1 className="text-stone-900 text-5xl font-bold leading-tight tracking-tighter mb-4">
              {article.title}
            </h1>
            <p className="text-stone-600 text-base">
              By {article.author} | Published on {new Date(article.published_date).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          <div className="prose prose-stone max-w-none text-lg leading-relaxed text-stone-800">
            {article.image_url && (
              <figure className="my-8">
                <img 
                  alt={article.title}
                  className="w-full rounded-md object-cover shadow-lg border border-stone-200" 
                  src={article.image_url}
                />
              </figure>
            )}
            
            <div dangerouslySetInnerHTML={{ __html: article.content }} />
          </div>

          <div className="mt-10 pt-6 border-t border-stone-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-lg text-stone-600">share</span>
                <span className="text-sm text-stone-600">{article.shares} shares</span>
              </div>
              <ShareButtons
                url={window.location.href}
                title={article.title}
                description={article.excerpt}
                onShare={() => handleEngagement('share')}
              />
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article;